import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss', '../search-bar-dropdown/search-bar-dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DropdownComponent implements OnChanges, AfterViewInit {

  @Input() isValid: boolean = false;
  @Input() selectValue: any;
  @Input() required: boolean = false;
  @Input() selectLabel: string;
  @Input() disabled: boolean = false;
  @Input() optionDisable: any;
  @Input() isSvgVisible: boolean = false;
  @Input() positionStrategy: 'fixed' | 'absolute' = 'fixed';

  @Output() selectValueEmit: EventEmitter<any> = new EventEmitter();
  @Output() selectionChange = new EventEmitter<number | null>();

  @ViewChild('hiddenElement') hiddenElement: ElementRef;
  @ViewChild('ngSelectRef', { read: ElementRef }) ngSelectElRef!: ElementRef;
  isFocused = false;

  private _data: Array<{ id: number; name: string; disabled?: boolean }> = [];
  private cleanup: (() => void)[] = [];
  private observer: MutationObserver | null = null;

  @Input()
  set data(value: Array<{ id: number; name: string; disabled?: boolean }>) {
    this._data = Array.isArray(value) ? [...value] : [];
  }

  get data(): Array<{ id: number; name: string; disabled?: boolean }> {
    return this._data;
  }

ngOnChanges(changes: SimpleChanges): void {
  if (changes['data'] || changes['optionDisable']) {
    this.data = this.data.map(item => ({
      ...item,
      disabled: this.optionDisable ? item.id === this.optionDisable : item.disabled ?? false
    }));
  }
}

  ngAfterViewInit(): void {
    document.getElementById('hidden')?.focus();
  }

onSelectionChange(selected: any): void {
  const selectedId = selected && typeof selected === 'object' ? selected.id : selected;
  const selectedItem = this.data.find(item => item.id === selectedId);

  if (selectedId === null || selectedId === undefined || selectedId === '' || selectedItem?.disabled) {
    setTimeout(() => { this.selectValue = null; });
    this.selectValueEmit.emit('');
    this.selectionChange.emit(null);
    return;
  }
  this.selectValue = selectedId;
  this.selectValueEmit.emit(selectedId);
  this.selectionChange.emit(selectedId);
}

clearSelection(): void {
  this.selectValue = null;
  this.selectValueEmit.emit('');
  this.selectionChange.emit(null);
}

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;
  }

  onOpen(ref: HTMLElement): void {
    this.isFocused = true;
    requestAnimationFrame(() => this.position());
    const fn = () => this.position();
    window.addEventListener('scroll', fn, true);
    window.addEventListener('resize', fn);
    this.cleanup = [
      () => window.removeEventListener('scroll', fn, true),
      () => window.removeEventListener('resize', fn)
    ];
  }

  onClose(): void {
    this.isFocused = false;
    this.observer?.disconnect();
    this.observer = null;
    this.cleanup.forEach(fn => fn());
    this.cleanup = [];
  }
  position() {
    const host = this.ngSelectElRef?.nativeElement as HTMLElement;
    if (!host) return;

    const panel = host.querySelector('.ng-dropdown-panel') as HTMLElement;
    if (!panel) return;

    this.applyStyles(panel, host);

    // Watch for ng-select resetting styles and re-apply
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => {
      this.applyStyles(panel, host);
    });

    this.observer.observe(panel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

applyStyles(panel: HTMLElement, host: HTMLElement) {
  const r = host.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - r.bottom;
  const spaceAbove = r.top;
  const maxPanelHeight = 242;

  const openUpward = spaceBelow < maxPanelHeight && spaceAbove > spaceBelow;
  const availableHeight = openUpward
    ? Math.min(spaceAbove, maxPanelHeight)
    : Math.min(spaceBelow, maxPanelHeight);

  this.observer?.disconnect();

  // Ensure measured panel height reflects current filtered content.
  panel.style.maxHeight = `${availableHeight}px`;
  const panelHeight = Math.min(panel.scrollHeight, availableHeight);

  const isFixed = this.positionStrategy === 'fixed';
  const topPos = isFixed
    ? (openUpward ? r.top - panelHeight : r.bottom)
    : (openUpward ? -panelHeight : host.offsetHeight);

  panel.style.cssText =
    `position:${this.positionStrategy}!important;` +
    `width:${isFixed ? r.width + 'px' : '100%'}!important;` +
    `left:${isFixed ? r.left + 'px' : '0'}!important;` +
    `top:${topPos}px!important;` +
    `bottom:unset!important;` +  
    `max-height:${availableHeight}px!important;` +
    `z-index:10!important;` +
    `overflow-y:auto!important;` +
    `display:block!important;` +
    `visibility:visible!important;` +
    `opacity:1!important;`;

  this.observer?.observe(panel, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class']
  });
}

  ngOnDestroy() { this.onClose(); }
}